import { Add as AddIcon } from '@mui/icons-material';
import Neutral from '@mui/icons-material/Face';
import Dissatisfied from '@mui/icons-material/SentimentDissatisfied';
import Satisfied from '@mui/icons-material/SentimentSatisfied';
import { Box, Typography, Card, Divider, Button, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { ObjectID } from 'bson';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';
import cartesian from '../../../../../util/cartesian';
import GSheetRuleModal from './GSheetRuleModal';
import styles from '../IntegrationsListPage.css';
import { IoTrashOutline as DeleteIcon, IoPencil as EditIcon } from 'react-icons/io5';

const messages = defineMessages({
  gsheetUrl: {
    id: 'IntegrationsSettings.GSheetUrl',
    defaultMessage: 'Sheet URL',
  },
  gsheetUrlRequired: {
    id: 'IntegrationsSettings.GSheetUrlRequired',
    defaultMessage: 'Please enter a sheet URL.',
  },
  surveysRequired: {
    id: 'IntegrationsSettings.GSheetSurveysRequired',
    defaultMessage: 'Please select some surveys.',
  },
  gsheetUrlFormat: {
    id: 'IntegrationsSettings.GSheetUrlFormat',
    defaultMessage: 'It must be a valid URL.',
  },
  survey: {
    id: 'IntegrationsSettings.GSheetSurvey',
    defaultMessage: 'Survey',
  },
  condition: {
    id: 'IntegrationsSettings.GSheetCondition',
    defaultMessage: 'Condition',
  },
  addRule: {
    id: 'IntegrationsSettings.GSheetAddRule',
    defaultMessage: 'Add a new rule',
  },
  conditionAllAll: {
    id: 'IntegrationsSettings.ConditionAllAll',
    defaultMessage: 'All responses',
  },
  conditionCommentedAll: {
    id: 'IntegrationsSettings.ConditionCommentedAll',
    defaultMessage: 'Only responses with comments',
  },
  conditionAllPromoters: {
    id: 'IntegrationsSettings.ConditionAllPromoters',
    defaultMessage: 'All "Satisfied"',
  },
  conditionCommentedPromoters: {
    id: 'IntegrationsSettings.ConditionCommentedPromoters',
    defaultMessage: 'Only "Satisfied" with comments',
  },
  conditionAllPassives: {
    id: 'IntegrationsSettings.ConditionAllPassives',
    defaultMessage: 'All "Neutral"',
  },
  conditionCommentedPassives: {
    id: 'IntegrationsSettings.ConditionCommentedPassives',
    defaultMessage: 'Only "Neutral" with comments',
  },
  conditionAllDetractors: {
    id: 'IntegrationsSettings.ConditionAllDetractors',
    defaultMessage: 'All "Dissatisfied"',
  },
  conditionCommentedDetractors: {
    id: 'IntegrationsSettings.ConditionCommentedDetractors',
    defaultMessage: 'Only "Dissatisfied" with comments',
  },
  all: {
    id: 'IntegrationsSettings.All',
    defaultMessage: 'All',
  },
  edit: {
    id: 'IntegrationsSettings.Edit',
    defaultMessage: 'Edit',
  },
  remove: {
    id: 'IntegrationsSettings.Remove',
    defaultMessage: 'Remove',
  },
  noRules: {
    id: 'IntegrationsSettings.GSheetNoRules',
    defaultMessage: 'No Google Sheets rules configured.',
  },
  gsheetUrl: {
    id: 'IntegrationsSettings.GSheetUrl',
    defaultMessage: 'Sheet URL',
  },
  surveys: {
    id: 'IntegrationsSettings.GSheetSurveys',
    defaultMessage: 'Surveys',
  },
  all: {
    id: 'IntegrationsSettings.All',
    defaultMessage: 'All',
  },
  cancel: {
    id: 'Confirm.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Confirm.Save',
    defaultMessage: 'Save',
  },
});

const DEFAULT_RULES = [
  {
    _id: new ObjectID().toString(),
    survey: null,
    condition: 'group:all,commented:false',
    gsheetUrl: '',
  },
];

const IntegrationConfigGSheet = () => {
  const { onIntegrationsPropertiesChange, integration, surveys } = useOutletContext();
  const { formatMessage } = useIntl();
  const { rules = DEFAULT_RULES } = integration;

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalRuleIdx, setModalRuleIdx] = React.useState(null);

  const openEditModal = idx => {
    setModalRuleIdx(idx);
    setModalOpen(true);
  };
  const openAddModal = () => {
    setModalRuleIdx(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalRuleIdx(null);
  };
  const handleModalSave = (rule, idx) => {
    let newRules;
    if (typeof idx === 'number') {
      newRules = rules.map((r, i) => (i === idx ? rule : r));
    } else {
      newRules = [...rules, rule];
    }
    onIntegrationsPropertiesChange(null, 'gsheets', { rules: newRules });
  };

  const icons = {
    Promoters: <Satisfied style={{ color: '#4fc44f' }} />,
    Passives: <Neutral style={{ color: '#f7c64b' }} />,
    Detractors: <Dissatisfied style={{ color: '#f66242' }} />,
  };

  const removeRule = idx => () => {
    const newRules = rules.filter((_, i) => i !== idx);
    onIntegrationsPropertiesChange(null, 'gsheets', { rules: newRules });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box className={styles.syncSurveysSection}>
        <Box className={styles.syncSurveysAddBtn}>
          <Button
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            className={styles.syncSurveysAddBtnInner}
            sx={{ fontWeight: 600 }}
          >
            {formatMessage(messages.addRule)}
          </Button>
        </Box>
        <Card variant="outlined" className={styles.syncSurveysTable}>
          <Box className={styles.syncSurveysHeader}>
            <Box className={styles.tableHeader}>{formatMessage(messages.survey)}</Box>
            <Box className={styles.tableHeaderActions} />
          </Box>
          {rules.length === 0 ? (
            <Box className={styles.syncSurveysEmpty}>
              <FormattedMessage
                id="IntegrationsSettings.GSheetNoRules"
                defaultMessage="No Google Sheets rules configured."
              />
            </Box>
          ) : (
            rules.map((rule, idx) => (
              <Box
                key={rule._id}
                className={styles.syncSurveysRow}
                onClick={e => {
                  if (e.target.closest('button')) return;
                  openEditModal(idx);
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') openEditModal(idx);
                }}
              >
                <Box className={styles.tableRow}>
                  {rule.survey
                    ? (surveys.find(s => s.id === rule.survey) || {}).name
                    : formatMessage(messages.all)}
                </Box>
                <Box className={styles.tableRowActions}>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      openEditModal(idx);
                    }}
                    color="primary"
                    size="small"
                    title={formatMessage(messages.edit)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      removeRule(idx)();
                    }}
                    color="error"
                    size="small"
                    title={formatMessage(messages.remove)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Card>
      </Box>
      <GSheetRuleModal
        open={modalOpen}
        onClose={closeModal}
        ruleIdx={modalRuleIdx}
        onSave={handleModalSave}
        rules={rules}
        surveys={surveys}
        messages={messages}
        cartesian={cartesian}
        icons={icons}
        formatMessage={formatMessage}
      />
    </Box>
  );
};

export default IntegrationConfigGSheet;
